<template>
  <component
    :is="cardComponent"
    v-bind="{ ...props }"
    :product-images="productImages"
    :product-title="productTitle"
    :is-configuration-inventory-out-of-stock-plp="
      isConfigurationInventoryOutOfStockPlp
    "
    :is-configuration-out-of-stock-plp="isConfigurationOutOfStockPlp"
    :product-price="productPrice"
    :product-link="productLink"
    :ring-size-material-category="ringSizeMaterialCategory"
    :ring-size-selected-item="ringSizeSelectedItem"
    :product-s-k-u="productSKU"
    :discount-value="discountValue"
    :add-to-cart-pending="addToCartPending"
    :in-wishlist="isProductInWishlist"
    :handle-filter="handleFilter"
    :set-ga-select-item-event="setGaSelectItemEvent"
    :update-wishlist="handleWishlist"
    :on-add-to-cart="onAddToCart"
    :show-out-of-stock="showOutOfStock"
    :statistic-item-list-name="statisticItemListName"
  />
</template>

<script lang="ts" setup>
import { CartItem, CatalogData, ColorPickerData } from "ecom-types";
import { object, string } from "yup";
import { useForm } from "vee-validate";
import { sizesSlugs, propertiesGroupSlugs } from "@/configs";
import { useProductFilter } from "~/composables/_useProductFilter";
import ProductListCarouselCardVertical from "./ProductListCarouselCardVertical.vue";
import ProductListCarouselCardHorizontal from "./ProductListCarouselCardHorizontal.vue";

const props = withDefaults(
  defineProps<{
    isCardVertical?: boolean;
    hover?: boolean;
    imageBrightnessFilter?: string;
    cardContentPosition?: string;
    product: CatalogData;
    plpType?: string;
    extractSetting?: boolean;
    productIndex: number;
    statisticItemListName?: string;
    statisticItemListId?: string;
    imageBackgroundColor?: ColorPickerData;
    isCardDrawer?: boolean;
    showOutOfStock?: boolean;
  }>(),
  {
    isCardVertical: false,
    imageBrightnessFilter: "100",
    cardContentPosition: "center",
    productIndex: 0,
    statisticItemListName: "",
    statisticItemListId: "",
    isCardDrawer: false,
    showOutOfStock: false,
  },
);

const currentProduct = ref<CatalogData>(props.product);

const { inWishlist, updateWishlist } = useWishlistV2();
const {
  productInfo,
  productLink,
  productPrice,
  productTitle,
  discountValue,
  isConfigurationInventoryOutOfStockPlp,
  isConfigurationOutOfStockPlp,
  productSKU,
  configurationInfo,
} = useProduct(props.product, sizesSlugs);
const {
  currentSku,
  result,
  additionalOptions,
  filteredMaterialsByCategories,
  setResultData,
  onFilterClick,
  onOptionsFilterClick,
} = useProductFilter(currentProduct, sizesSlugs);

setResultData();

const { openSystemDialog } = useSystemDialogs();
const { setItemListContext } = useItemListContext();
const { t } = useI18n();
const { addToCart, cartItems } = useCart();
const { selectItemEvent, addToCartEvent } = useEvents();
const { $api } = useNuxtApp();

const addToCartPending = ref<boolean>(false);

const productImages = computed(() => {
  return getProductImages(props.product);
});

const isProductInWishlist = computed(() =>
  Boolean(
    inWishlist.value({
      sku: currentSku.value,
      vendor_model: props.product.vendor_model,
    }),
  ),
);

const ringSizeMaterialCategory = computed(() => {
  return (
    filteredMaterialsByCategories.value.find(
      (el) => el?.slug === propertiesGroupSlugs.ringSize,
    ) || null
  );
});

const ringSizeSelectedItem = computed(() => {
  if (!ringSizeMaterialCategory.value) return null;

  return result.value?.[ringSizeMaterialCategory.value.slug]
    ? result.value?.[ringSizeMaterialCategory.value.slug]
    : additionalOptions.value?.[ringSizeMaterialCategory.value.slug];
});

const cardComponent = computed(() => {
  return props.isCardVertical
    ? ProductListCarouselCardVertical
    : ProductListCarouselCardHorizontal;
});

const { validate } = useProductValidation();

function useProductValidation() {
  const productSchema = computed(() => {
    if (
      ringSizeMaterialCategory.value &&
      (Object.keys(additionalOptions.value)?.includes(
        ringSizeMaterialCategory.value.slug,
      ) ||
        Object.keys(result.value)?.includes(
          ringSizeMaterialCategory.value.slug,
        ))
    ) {
      return object().shape({
        [ringSizeMaterialCategory.value.slug]: string().required(
          t("pleaseSelectSize"),
        ),
      });
    } else {
      return {};
    }
  });

  const { validate } = useForm({
    validationSchema: productSchema,
  });

  return {
    validate,
  };
}

function setGaSelectItemEvent(shouldCloseDrawer = true) {
  setItemListContext(
    props.statisticItemListName || "",
    props.statisticItemListId || "",
  );
  const { itemVariant } = useActiveMaterials(props.product);
  selectItemEvent(props.product, {
    index: props.productIndex,
    item_list_name: props.statisticItemListName,
    item_list_id: props.statisticItemListId,
    item_variant: itemVariant.value,
  });

  if (props.isCardDrawer && shouldCloseDrawer) {
    const checkoutStore = useCheckoutStore();
    checkoutStore.isCartOpen = false;
  }
}

async function updateConfiguration(): void {
  const { data } = await useAsyncCachedData(currentSku.value, () => {
    return $api.products.getConfiguration(currentSku.value);
  });

  configurationInfo.value = data.value.data;

  const foundConfigurationImage = configurationInfo.value.medias.find(
    (image) => image.position === "PLP",
  );

  productInfo.value.variant_plp_image.image = {
    ...foundConfigurationImage.file,
  };
}

function handleFilter(data) {
  if (result.value?.[ringSizeMaterialCategory.value?.slug]) {
    onFilterClick(data);
  } else {
    onOptionsFilterClick(data);
  }
}

async function onAddToCart(openCardDrawer = false) {
  const { valid } = await validate();

  if (!valid) {
    return;
  }

  if (props.isCardDrawer) {
    openCardDrawer = true;
  }

  try {
    addToCartPending.value = true;

    const options: { [key: string]: string | number | object } = {};
    const imageIdListIds = [productImages.value[0].id];

    if (Object.values(additionalOptions.value).length) {
      options.materials = Object.entries(additionalOptions.value).map(
        ([key, val]) => {
          return {
            sku: val?.sku,
            title: val?.currentTranslation?.title,
            material_category_title: filteredMaterialsByCategories.value.find(
              (el) => el.slug === key,
            )?.currentTranslation?.title,
            material_category_id: val?.material_category_id,
            material_category_sku:
              productInfo.value?.product_material_categories?.find(
                (category) => category.id === val?.material_category_id,
              ),
          };
        },
      );
    }

    const fbEventId = generateEventId("add_to_cart");
    const cartResult = await addToCart(
      {
        configuration_id: currentSku.value,
        vendor_model: productInfo.value?.vendor_model,
        images_ids: imageIdListIds,
        images: [],
        options,
        event_id: fbEventId,
      },
      openCardDrawer,
    );
    if (!cartResult?.error && productInfo.value) {
      addToCartEvent({
        product_id: productInfo.value.id.toString(),
        configuration_id: configurationInfo.value?.configuration_id || currentSku.value || "",
        collection_id: productInfo.value?.collections?.[0]?.id || 0,
        product_sku: productInfo.value?.vendor_model || "",
        product_price: productPrice.value,
        productData: productInfo.value,
        configurationData:
          configurationInfo.value ||
          (productInfo.value as CatalogData)?.selected_config ||
          productInfo.value?.default_config ||
          undefined,
        productIndex: cartItems.value.length ? cartItems.value.length - 1 : 0,
        extraData: { fb_event_id: fbEventId },
      });
    }
  } catch (error) {
    if (error instanceof ImageMergeError) {
      openSystemDialog({
        type: "alert",
        title: `${t("error")}!`,
        text: `${t("failedLoadImage")}!`,
      });
    }
  } finally {
    addToCartPending.value = false;
  }
}

function handleWishlist() {
  const options: Record<string, any> = {};
  const ringSizeMaterialCategorySlug = ringSizeMaterialCategory.value?.slug;
  if (ringSizeMaterialCategorySlug && result.value?.[ringSizeMaterialCategorySlug]) {
    options.is_size_selected = true;
  }
  updateWishlist({
    sku: currentSku.value,
    vendor_model: props.product.vendor_model,
    options,
  });
  // Don't close cart drawer when adding to wishlist
  setGaSelectItemEvent(false);
}

watch(() => currentSku.value, updateConfiguration);
</script>
